import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
    Box,
    Button,
    Chip,
    CircularProgress,
    makeStyles,
    Menu,
    MenuItem,
} from '@material-ui/core';
import i18n from 'i18n-js';

import { PopupConfirmation } from 'components/shared';
import { theme } from 'config/theme';
import {
    formatGuestName,
    formatGuestsCount,
    formatGuestsLabel,
    formatReservationDate,
} from 'helpers/reservationHelper';
import { useAppSelector } from 'index';
import globalStore from 'store/globalStore';
import reservationsStore from 'store/reservationsStore';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { useReservationMaps } from '../hooks';
import ReservationDetailsPanel from './ReservationDetailsPanel';
import ReservationsTable, { ReservationColumn } from './ReservationsTable';

const useStyles = makeStyles({
    declineOption: {
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        width: 260,
        padding: '10px 24px',
    },
    declinePill: {
        minWidth: 100,
        textAlign: 'center',
        fontSize: 14,
        lineHeight: '18px',
        borderRadius: 100,
        border: `1px solid ${theme.palette.error.main}`,
        color: theme.palette.error.main,
        backgroundColor: theme.palette.common.white,
    },
    declineHelper: {
        fontSize: 12,
        color: theme.palette.text.disabled,
    },
});

export default function RequestsTab() {
    const classes = useStyles();
    const history = useHistory();
    const dispatch = useThunkDispatch();
    const { requests, selectedRequestId } = useAppSelector((state) => state.reservations);
    const isRequestsLoaded = useAppSelector(
        (state) => state.reservations.requestsRestaurantId === state.user.currentRestaurantId
    );
    const { tables } = useReservationMaps();
    const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
    const [isDeclineDialogOpen, setIsDeclineDialogOpen] = useState(false);
    const [isDeclining, setIsDeclining] = useState(false);
    const selected = requests.find((request) => request.id === selectedRequestId);
    const now = Date.now();

    const isOverdue = (request: (typeof requests)[number]) =>
        Date.parse(request.reservationStartDateTime) < now;

    const columns: ReservationColumn[] = [
        {
            id: 'dateTime',
            label: i18n.t('reservations.columns.dateTime'),
            getText: (request) =>
                formatReservationDate(request.reservationStartDateTime, 'dd.MM · HH:mm'),
            getSortValue: (request) => request.reservationStartDateTime,
            render: (request) => (
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <span>
                        {formatReservationDate(request.reservationStartDateTime, 'dd.MM · HH:mm')}
                    </span>
                    {isOverdue(request) && (
                        <Chip
                            size="small"
                            label={i18n.t('reservations.overdue')}
                            style={{
                                backgroundColor: theme.palette.error.main,
                                color: theme.palette.common.white,
                            }}
                        />
                    )}
                </Box>
            ),
        },
        { id: 'name', label: i18n.t('reservations.columns.name'), getText: formatGuestName },
        {
            id: 'guests',
            label: i18n.t('reservations.columns.guests'),
            getText: formatGuestsCount,
            getSortValue: (request) => request.guestsNumber,
        },
        {
            id: 'contact',
            label: i18n.t('reservations.columns.contact'),
            getText: (request) => request.phone || request.email || '',
        },
        {
            id: 'source',
            label: i18n.t('reservations.columns.source'),
            getText: (request) => i18n.t(`reservations.channels.${request.channel}`),
        },
        {
            id: 'received',
            label: i18n.t('reservations.columns.received'),
            getText: (request) => formatReservationDate(request.createDate, 'dd.MM HH:mm'),
            getSortValue: (request) => request.createDate,
        },
    ];

    const handleDecline = async (id: string) => {
        if (isDeclining) {
            return;
        }
        setIsDeclining(true);
        try {
            const result = await dispatch(reservationsStore.actionCreators.declineRequest(id));
            dispatch(
                result === 'declined'
                    ? globalStore.actionCreators.showToaster(
                          'success',
                          i18n.t('reservations.requestDeclined')
                      )
                    : globalStore.actionCreators.showToaster(
                          'error',
                          i18n.t('reservations.alreadyHandled')
                      )
            );
        } catch (error) {
            dispatch(globalStore.actionCreators.showToaster('error', error));
        } finally {
            setIsDeclining(false);
        }
    };

    if (!isRequestsLoaded) {
        return (
            <Box display="flex" flex={1} alignItems="center" justifyContent="center">
                <CircularProgress size={48} style={{ color: theme.palette.primary.main }} />
            </Box>
        );
    }

    return (
        <Box display="flex" flex={1} minHeight={0} pl={3}>
            <ReservationsTable
                columns={columns}
                reservations={requests}
                sortLast={isOverdue}
                selectedId={selectedRequestId}
                onSelect={(id) => dispatch(reservationsStore.actionCreators.selectRequest(id))}
                emptyText={i18n.t('reservations.noOpenRequests')}
            />
            {selected && (
                <ReservationDetailsPanel
                    reservation={selected}
                    tables={tables}
                    onClose={() => dispatch(reservationsStore.actionCreators.selectRequest(null))}
                    footer={
                        <>
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={isDeclining}
                                onClick={(event) => setMoreAnchor(event.currentTarget)}
                            >
                                {i18n.t('reservations.more')}
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() =>
                                    history.push(`/reservations/requests/${selected.id}/process`)
                                }
                            >
                                {i18n.t('reservations.processRequest')}
                            </Button>
                        </>
                    }
                />
            )}
            <Menu
                anchorEl={moreAnchor}
                open={!!moreAnchor}
                onClose={() => setMoreAnchor(null)}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <MenuItem
                    className={classes.declineOption}
                    onClick={() => {
                        setMoreAnchor(null);
                        setIsDeclineDialogOpen(true);
                    }}
                >
                    <span className={classes.declinePill}>{i18n.t('reservations.decline')}</span>
                    <span className={classes.declineHelper}>
                        {i18n.t('reservations.declineHelper')}
                    </span>
                </MenuItem>
            </Menu>
            {selected && (
                <PopupConfirmation
                    open={isDeclineDialogOpen}
                    isDismissible
                    close={() => setIsDeclineDialogOpen(false)}
                    title={i18n.t('reservations.declineTitle')}
                    description={i18n.t('reservations.declineDescription', {
                        name: formatGuestName(selected),
                        dateTime: formatReservationDate(
                            selected.reservationStartDateTime,
                            'EEE dd.MM.yyyy, HH:mm'
                        ),
                        guests: formatGuestsLabel(formatGuestsCount(selected)),
                    })}
                    activeBtn={i18n.t('reservations.decline')}
                    action={() => handleDecline(selected.id)}
                />
            )}
        </Box>
    );
}
